const DIRECTUS_URL = process.env.REACT_APP_DIRECTUS_URL;
const DIRECTUS_TOKEN = process.env.REACT_APP_DIRECTUS_TOKEN;

const directusFetch = async (path) => {
  const response = await fetch(`${DIRECTUS_URL}${path}`, {
    headers: {
      Authorization: `Bearer ${DIRECTUS_TOKEN}`,
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error(`Directus error: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Fetch booking transfer records from Directus
 * @returns {Promise<Array>} - Array of booking transfer records
 */
export const fetchBookingTransfers = async () => {
  try {
    const filter = JSON.stringify({
      IsDeleted: { _eq: false },
    });

    const result = await directusFetch(
      `/items/BookingTransfer?filter=${encodeURIComponent(filter)}&limit=-1&fields=*`
    );

    return result.data || [];
  } catch (error) {
    console.error("Error fetching booking transfers from Directus:", error);
    return [];
  }
};

/**
 * Fetch allocated booking transfers by joining with bookings table
 * @returns {Promise<Array>} - Array of booking transfer records with booking data
 */
export const fetchAllocatedBookingTransfers = async () => {
  try {
    // Step 1: Fetch all non-deleted booking transfers
    const transferFilter = JSON.stringify({
      IsDeleted: { _eq: false },
    });

    const transferResult = await directusFetch(
      `/items/BookingTransfer?filter=${encodeURIComponent(transferFilter)}&limit=-1&fields=*`
    );

    const transfers = transferResult.data || [];
    console.log("All booking transfers:", transfers);

    if (transfers.length === 0) {
      return [];
    }

    // Step 2: Get all unique OldWingsId values
    const wingsIds = transfers
      .map(t => t.OldWingsId)
      .filter(id => id); // Remove null/undefined

    console.log("Wings IDs to search:", wingsIds);

    if (wingsIds.length === 0) {
      console.log("No valid Wings IDs found, returning transfers without booking data");
      return transfers;
    }

    const bookingFilter = JSON.stringify({
      WingsId: { _in: wingsIds }
    });

    const bookingResult = await directusFetch(
      `/items/booking?filter=${encodeURIComponent(bookingFilter)}&limit=-1&fields=*`
    );

    const bookings = bookingResult.data || [];
    console.log("Bookings for transfers:", bookings.length);

    const bookingMap = new Map();
    bookings.forEach(booking => {
      if (booking.WingsId) bookingMap.set(booking.WingsId, booking);
    });

    // Determine completed transfers — new customer name is now in the booking table
    const completedTransfers = transfers
      .filter(t => bookingMap.has(t.OldWingsId))
      .map(transfer => {
        const bookingData = bookingMap.get(transfer.OldWingsId);
        const currentName = (bookingData?.customer_name || "").toLowerCase().trim();
        const oldFirstName = (transfer.OldCustomerFirstName || "").toLowerCase().trim();
        const newFirstName = (transfer.NewCustomerFirstName || "").toLowerCase().trim();

        // Transfer is complete if:
        // 1. Old customer name is gone from booking, OR
        // 2. New customer name is now in the booking
        const oldNameGone = oldFirstName && !currentName.includes(oldFirstName);
        const newNamePresent = newFirstName && currentName.includes(newFirstName);
        const isCompleted = oldNameGone || newNamePresent;

        return { ...transfer, bookingData, _hasBookingData: true, _isCompleted: !!isCompleted };
      })
      .filter(t => t._isCompleted);

    console.log(`Found ${completedTransfers.length} completed transfers out of ${transfers.length} total`);
    return completedTransfers;
  } catch (error) {
    console.error("Error fetching booking transfers with booking data:", error);
    // Fallback to fetching all transfers without booking data
    return fetchBookingTransfers();
  }
};


/**
 * Check if a Wings ID has a booking transfer
 * @param {string} wingsId - Wings ID to check
 * @param {Array} bookingTransfers - Array of booking transfer records
 * @param {Object} currentBooking - Current booking data to check if transfer is completed
 * @returns {Object|null} - Booking transfer record if found and not completed, null otherwise
 */
export const checkBookingTransferStatus = (wingsId, bookingTransfers, currentBooking = null) => {
  if (!wingsId || !bookingTransfers || bookingTransfers.length === 0) {
    return null;
  }

  // Check both OldWingsId and NewWingsId
  const transfer = bookingTransfers.find(
    (transfer) =>
      transfer.OldWingsId === wingsId || transfer.NewWingsId === wingsId
  );

  if (!transfer) {
    return null;
  }

  // If we have current booking data, check if the transfer has been completed
  // by comparing customer names
  if (currentBooking) {
    const currentFirstName = (currentBooking.customer_name || "").toLowerCase().trim();
    const currentLastName = (currentBooking.LastName || "").toLowerCase().trim();
    const currentFullName = `${currentFirstName} ${currentLastName}`.trim();
    
    // Get new customer names from transfer
    const newFirstName = (transfer.NewCustomerFirstName || "").toLowerCase().trim();
    const newLastName = (transfer.NewCustomerLastName || "").toLowerCase().trim();
    const newFullName = (transfer.NewCustomerFullName || transfer.NewCustomerName || "").toLowerCase().trim();
    
    console.log("Checking transfer completion:", {
      currentFirstName,
      currentLastName,
      currentFullName,
      newFirstName,
      newLastName,
      newFullName,
      transfer
    });
    
    // Check if names match (multiple ways to be thorough)
    const isCompleted = 
      // Check full name match
      (newFullName && currentFullName.includes(newFullName)) ||
      (newFullName && newFullName.includes(currentFullName)) ||
      // Check first name match
      (newFirstName && currentFirstName === newFirstName) ||
      // Check if current name matches new first + last
      (newFirstName && newLastName && currentFullName.includes(`${newFirstName} ${newLastName}`));
    
    if (isCompleted) {
      console.log("Transfer is completed!");
      // Return transfer object with a flag indicating it's completed
      return { ...transfer, _isCompleted: true };
    }
  }

  return transfer;
};
